import {
  pairHasOpenIssue,
  useTaskActivitiesForServiceRequest,
} from '@commenda/api/tasks';
import { Button, Icons } from '@commenda/kakashi';
import { TaskParty, TaskStatus } from '@prisma/client';
import { useRouter } from 'next/router';
import { WORK_REQUEST_CONFIG } from '../../../incorporation';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { getTaskLabel } from '../../task-labels';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { CaCorpIncorporationTask } from '../config';

export const FillCompanyDetails = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const router = useRouter();
  const { role, serviceRequest } = context;
  const currentURL = window.location.href;

  const { data: activityFeed } = useTaskActivitiesForServiceRequest(
    serviceRequest?.id
  );

  // Own task is the fixer; issues are reported from the sibling review task.
  const reviewTask = serviceRequest?.taskGroups
    ?.find((srtg) => srtg.id === task.taskGroupId)
    ?.tasks?.find(
      (srt) =>
        srt.legacyServiceRequestTaskTaskId ===
        CaCorpIncorporationTask.REVIEW_CORPORATION_DETAILS
    );
  const hasOpenReportedIssue = pairHasOpenIssue(
    activityFeed?.activities,
    task,
    reviewTask
  );

  // TODO: Add Notification to this app
  const openWorkRequest = () => {
    router.push({
      pathname: `${WORK_REQUEST_CONFIG[task.workRequest.requestType].urlPrefix}/${task.workRequestId}`,
      query: {
        previousURL: currentURL,
      },
    });
  };

  const getLabel = (role: TaskParty, status: TaskStatus) =>
    getTaskLabel(
      'EM_INC_CA',
      task.legacyServiceRequestTaskTaskId,
      role,
      status,
      task.title
    ) ?? task.title;

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      if (hasOpenReportedIssue && state.status !== TaskStatus.COMPLETED) {
        const issueMap = {
          [TaskParty.CLIENT]: {
            actions: (
              <Button size="sm" variant="secondary" onClick={openWorkRequest}>
                Fix Reported Issues
                <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Issues marked in questionnaire"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskParty.AGENT]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="Client fixing the reported issues"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        };
        const issueConfig = issueMap[state.role];
        if (issueConfig) return issueConfig;
      }

      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <Button size="sm" onClick={openWorkRequest} variant="primary">
                Start <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.CLIENT, TaskStatus.TO_DO)}
              />
            ),
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: (
              <Button size="sm" variant="secondary" onClick={openWorkRequest}>
                Continue <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.CLIENT, TaskStatus.IN_PROGRESS)}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button size="sm" variant="secondary" onClick={openWorkRequest}>
                View <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.CLIENT, TaskStatus.COMPLETED)}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.AGENT, TaskStatus.TO_DO)}
              />
            ),
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.AGENT, TaskStatus.IN_PROGRESS)}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.AGENT, TaskStatus.COMPLETED)}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
    </div>
  );
};
